import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [realisations, setRealisations] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  useEffect(() => {
    fetchLatestRealisations();
  }, []);

  const fetchLatestRealisations = async () => {
    try {
      setLoading(true);
      // Récupérer les 6 dernières réalisations publiées
      const response = await fetch(`${BACKEND_URL}/api/realisations?limit=6`);
      const data = await response.json();
      
      setRealisations(data.realisations || []);
    } catch (error) {
      console.error('Error fetching realisations:', error);
      setRealisations([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="realisations" className="py-24 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-[#FF6600] font-semibold text-sm uppercase tracking-wider">Nos Réalisations</span>
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mt-3 mb-4">
            Chantiers récents
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Découvrez nos dernières installations d'équipements de quai partout en France
          </p>
        </div>

        {/* Gallery Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF6600] mx-auto"></div>
            <p className="text-gray-600 mt-4">Chargement des réalisations...</p>
          </div>
        ) : realisations.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p>Aucune réalisation disponible pour le moment</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {realisations.map((item) => (
                <a
                  key={item.id}
                  href={`/realisations/${item.slug}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/realisations/${item.slug}`);
                  }}
                  className="relative overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer aspect-square group block no-underline"
                >
                  <img
                    src={item.main_image}
                    alt={item.title}
                    data-testid={`realisation-card-${item.id}`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
                    <span className="inline-block bg-[#FF6600] text-white text-xs font-semibold px-2 py-1 rounded mb-2 self-start">
                      {item.category}
                    </span>
                    <p className="text-white font-bold text-sm mb-1 line-clamp-2">{item.title}</p>
                    <p className="text-white/80 text-xs">{item.city} • {item.date || '2026'}</p>
                  </div>
                </a>
              ))}
            </div>

            <div className="text-center">
              <button
                onClick={() => navigate('/realisations')}
                className="inline-block bg-[#FF6600] hover:bg-[#e55a00] text-white font-bold py-4 px-8 rounded-lg transition-colors shadow-lg hover:shadow-xl"
              >
                Voir toutes nos réalisations
              </button>
            </div>
          </>
        )}

        {/* Stats Bar */}
        <div className="mt-20 bg-gradient-to-r from-[#020d2a] to-[#03152f] rounded-2xl p-12 shadow-2xl">
          <div className="grid md:grid-cols-2 gap-8 text-center text-white">
            <div>
              <p className="text-4xl font-bold text-[#FF6600] mb-2">7+</p>
              <p className="text-gray-300">Années d'expérience</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-[#FF6600] mb-2">24-48h</p>
              <p className="text-gray-300">Dépannage selon localisation</p>
            </div>
          </div>
        </div>

        {/* Lightbox Modal */}
        {selectedImage && (
          <div
            data-testid="realisation-lightbox"
            role="dialog"
            aria-modal="true"
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <button
              data-testid="lightbox-close-button"
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 text-white hover:text-[#FF6600] transition-colors"
            >
              <X className="w-8 h-8" />
            </button>

            <div className="max-w-6xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
              <img
                data-testid="lightbox-image"
                src={selectedImage.main_image}
                alt={selectedImage.title}
                className="w-full h-auto max-h-[70vh] object-contain rounded-lg"
              />
              <div className="bg-white rounded-b-lg p-6 mt-2">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">{selectedImage.title}</h3>
                <div className="flex items-center gap-4 text-gray-600 mb-3">
                  <span className="font-semibold">{selectedImage.city}</span>
                  <span>•</span>
                  <span>{selectedImage.category}</span>
                  <span>•</span>
                  <span>{selectedImage.date || '2026'}</span>
                </div>
                {selectedImage.solution_description && (
                  <p className="text-gray-700">{selectedImage.solution_description}</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Gallery;
